import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Accordion, Card } from 'react-bootstrap'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'
import { ClockIcon, PinIcon, VenueIcon, ArrowDownThinIcon } from 'images'
import moment from 'moment'
import { formatEventSubheadlineLocation, treatAsProductEntry } from 'utils/helpers'
import useCustomTranslation from 'hooks/useCustomTranslation'

export default function EventDetailsAccordion({ eventInfo, crEvent, isLoading }) {
  const [activeKey, setActiveKey] = useState(null)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [showDescriptionToggle, setShowDescriptionToggle] = useState(false)
  const descriptionRef = useRef(null)
  const isOpen = activeKey === 'event-details'
  const { t } = useTranslation()
  const { getTranslation } = useCustomTranslation(eventInfo)
  // Prisma-format entries (category 'product' or 'prisma') are products —
  // no date, city
  // or venue, and product labels. Entries with real venues and cities are
  // actual events and keep their meta line and event wording everywhere.
  // Prisma-tier partners keep their campaign behavior throughout.
  const isProduct = treatAsProductEntry(eventInfo.category)

  useEffect(() => {
    setDescriptionExpanded(false)
  }, [crEvent?.description])

  useEffect(() => {
    if (!isOpen || !crEvent?.description) {
      setShowDescriptionToggle(false)
      return
    }

    const descriptionNode = descriptionRef.current
    if (!descriptionNode) return

    const checkDescriptionOverflow = () => {
      const collapsedClass = 'event-details-accordion__description--collapsed'
      const wasCollapsed = descriptionNode.classList.contains(collapsedClass)
      if (!wasCollapsed) descriptionNode.classList.add(collapsedClass)
      const hasOverflow = descriptionNode.scrollHeight - descriptionNode.clientHeight > 1
      if (!wasCollapsed) descriptionNode.classList.remove(collapsedClass)

      setShowDescriptionToggle(hasOverflow)
      if (!hasOverflow && descriptionExpanded) {
        setDescriptionExpanded(false)
      }
    }

    // Defer calculation until accordion/layout finishes painting.
    const initialRafId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(checkDescriptionOverflow)
    })

    const resizeObserver = new ResizeObserver(checkDescriptionOverflow)
    resizeObserver.observe(descriptionNode)

    return () => {
      window.cancelAnimationFrame(initialRafId)
      resizeObserver.disconnect()
    }
  }, [isOpen, crEvent?.description, descriptionExpanded])

  return (
    <Accordion className="event-details-accordion" activeKey={activeKey} onSelect={setActiveKey}>
      <Card>
        <Accordion.Toggle eventKey="event-details">
          <div className="toggle-wrapper">
            <p className="insights-header__text d-flex">
                <span className="mr-2">
                  <span>
                    <ClockIcon className="mr-1" height={12} width={12} />
                  </span>

                  {isProduct && <span className="mr-1">
                    {t('Recommendations.eventRow.productDateLabel')}
                  </span>}

                  <time dateTime={moment.utc(eventInfo.date).format('DD.MM.YYYY, HH:mm')}>
                    {' '}
                    {/* FIXME: Inconsistent time parsing & formatting? */}
                    {moment.utc(eventInfo.date).format(isProduct ? 'DD.MM.YYYY' : 'DD.MM.YYYY, HH:mm')}{' '}
                    {/* FIXME: Inconsistent time parsing & formatting? */}
                  </time>
                </span>

                {!isProduct && 
                  <>
                  {crEvent?.city && (
                    <span className="mr-2">
                      <span>
                        <PinIcon className="mr-1" height={12} width={17} />
                      </span>

                      <span>
                        {formatEventSubheadlineLocation({
                          city: crEvent?.city,
                        })}
                      </span>
                    </span>
                  )}

                  {crEvent?.hall && crEvent?.venue && (
                    <span>
                      <span>
                        <VenueIcon className="mr-1" height={12} width={17} />
                      </span>

                      <span>
                        {formatEventSubheadlineLocation({
                          hall: crEvent?.hall,
                          venue: crEvent?.venue,
                        })}
                      </span>
                    </span>
                  )}
                  </>
                }
            </p>

            <div className="d-flex align-items-center gap-1">
              <p className="pt-1">
                {isOpen
                  ? getTranslation('Event.hideDetails')
                  : !isProduct
                    ? getTranslation('Event.eventDetails')
                    : t('Event.productDetails')}
              </p>

              <ArrowDownThinIcon
                height={16}
                width={16}
                style={{
                  transform: isOpen ? 'rotate(180deg)' : undefined,
                  transition: 'transform 0.3s ease',
                }}
              />
            </div>
          </div>
        </Accordion.Toggle>

        <Accordion.Collapse eventKey="event-details">
          <Card.Body>
            {isLoading ? (
              <LoadingSpinner size={SpinnerSize.SMALL} />
            ) : crEvent ? (
              <div>
                {eventInfo.eid && eventInfo.eid.includes('-sim-') && (
                  <div className="mb-3 mt-3" style={{ color: '#616060', textAlign: 'left' }}>
                    {!isProduct ? getTranslation('Event.manual') : t('Event.manualProduct')}
                  </div>
                )}

                <div className="event-details-accordion__line">
                  {crEvent.description && (
                    <div className="mb-4">
                      <p className="section-title">
                        {!isProduct
                          ? getTranslation('Event.eventDescription')
                          : t('Event.productDescription')}
                      </p>

                      <p
                        ref={descriptionRef}
                        className={`mb-0 event-details-accordion__description ${!descriptionExpanded ? 'event-details-accordion__description--collapsed' : ''}`}
                      >
                        {crEvent.description}
                      </p>
                      {showDescriptionToggle && (
                        <button
                          type="button"
                          className="event-details-accordion__read-more-btn"
                          onClick={() => setDescriptionExpanded((prev) => !prev)}
                        >
                          {descriptionExpanded
                            ? t('Simulation.readLess')
                            : t('Simulation.readMore')}
                        </button>
                      )}
                    </div>
                  )}

                  {crEvent.lineups_raw?.length > 0 && (
                    <div className="mb-4">
                      <p className="section-title">{t('Simulation.artists')}</p>

                      <ul className="artist-list">
                        {crEvent.lineups_raw.map((l, i) => (
                          <li key={i} className="artist-item">
                            <span>{l.artist_name}</span>

                            <span className="artist-item__role">
                              {l.client_role_mapped && ` ${l.client_role_mapped}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {crEvent.setlists_raw?.length > 0 && (
                    <div>
                      <p className="section-title">{t('Simulation.programme')}</p>

                      <ul className="setlist-list">
                        {crEvent.setlists_raw.map((s, i) => (
                          <li key={i} className="setlist-item">
                            <p className="number">{i + 1}</p>

                            <div>
                              <p className="composer">{s.composer && s.composer}</p>

                              <p className="title">{s.title}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!crEvent.description &&
                    !crEvent.lineups_raw?.length &&
                    !crEvent.setlists_raw?.length && <p>{t('Event.noDescription')}</p>}
                </div>
              </div>
            ) : (
              <p>{t('Event.noDescription')}</p>
            )}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  )
}

EventDetailsAccordion.propTypes = {
  eventInfo: PropTypes.object.isRequired,
  crEvent: PropTypes.object,
  isLoading: PropTypes.bool,
}
